import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BankaHesaplari = () => {
  const [activeTab, setActiveTab] = useState('bakiye'); // 'bakiye', 'hesaplar', 'gecmis'
  const [hesaplar, setHesaplar] = useState([]);
  const [bakiyeGecmisi, setBakiyeGecmisi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Banka hesabı formu
  const [showHesapForm, setShowHesapForm] = useState(false);
  const [editingHesapId, setEditingHesapId] = useState(null);
  const [hesapFormData, setHesapFormData] = useState({
    kisa_ad: '',
    banka_adi: '',
    iban: '',
    swift: '',
    para_birimi: 'TL'
  });

  // Günlük bakiye girişi - dinamik satırlar
  const [bakiyeSatirlari, setBakiyeSatirlari] = useState([
    { id: 1, banka_hesap_id: '', banka_adi: '', para_birimi: 'TL', bakiye: '' }
  ]);
  const [notlar, setNotlar] = useState('');
  const [kurUsd, setKurUsd] = useState(0);
  const [kurEur, setKurEur] = useState(0);

// Bakiye geçmişi düzenleme
  const [showEditBakiyeModal, setShowEditBakiyeModal] = useState(false);
  const [editingBakiye, setEditingBakiye] = useState(null);
  const [editBakiyeSatirlari, setEditBakiyeSatirlari] = useState([]);
  const [editKurUsd, setEditKurUsd] = useState(0);
  const [editKurEur, setEditKurEur] = useState(0);
  const [editNotlar, setEditNotlar] = useState('');

  useEffect(() => {
    fetchHesaplar();
    fetchBakiyeGecmisi();
    fetchKurlar();
  }, []);

  const fetchHesaplar = async () => {
    try {
      const response = await axios.get(`${API}/banka-hesaplari`);
      setHesaplar(response.data);
    } catch (error) {
      console.error('Banka hesapları yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBakiyeGecmisi = async () => {
    try {
      const response = await axios.get(`${API}/gunluk-bakiye`);
      setBakiyeGecmisi(response.data);
    } catch (error) {
      console.error('Bakiye geçmişi yüklenirken hata:', error);
    }
  };

  const fetchKurlar = async () => {
    try {
      const response = await axios.get(`${API}/exchange-rates`);
      if (response.data) {
        setKurUsd(response.data.USD || 0);
        setKurEur(response.data.EUR || 0);
      }
    } catch (error) {
      console.error('Kurlar yüklenirken hata:', error);
    }
  };

  // Banka Hesabı İşlemleri
  const handleHesapSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingHesapId) {
        await axios.put(`${API}/banka-hesaplari/${editingHesapId}`, hesapFormData);
      } else {
        await axios.post(`${API}/banka-hesaplari`, hesapFormData);
      }
      setHesapFormData({
        kisa_ad: '',
        banka_adi: '',
        iban: '',
        swift: '',
        para_birimi: 'TL'
      });
      setEditingHesapId(null);
      setShowHesapForm(false);
      fetchHesaplar();
    } catch (error) {
      console.error('Banka hesabı kaydedilirken hata:', error);
      alert('Banka hesabı kaydedilemedi');
    }
  };

  const handleHesapDelete = async (id) => {
    if (window.confirm('Bu banka hesabını silmek istediğinize emin misiniz?')) {
      try {
        await axios.delete(`${API}/banka-hesaplari/${id}`);
        fetchHesaplar();
      } catch (error) {
        console.error('Banka hesabı silinirken hata:', error);
        alert('Banka hesabı silinemedi');
      }
    }
  };

  const handleHesapEdit = (hesap) => {
    setHesapFormData({
      kisa_ad: hesap.kisa_ad,
      banka_adi: hesap.banka_adi,
      iban: hesap.iban,
      swift: hesap.swift || '',
      para_birimi: hesap.para_birimi
    });
    setEditingHesapId(hesap.id);
    setShowHesapForm(true);
    // Sayfayı yukarı kaydır
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFormCancel = () => {
    setHesapFormData({
      kisa_ad: '',
      banka_adi: '',
      iban: '',
      swift: '',
      para_birimi: 'TL'
    });
    setEditingHesapId(null);
    setShowHesapForm(false);
  };

  // Dinamik Satır İşlemleri
  const handleSatirEkle = () => {
    const yeniId = Math.max(...bakiyeSatirlari.map(s => s.id), 0) + 1;
    setBakiyeSatirlari([
      ...bakiyeSatirlari,
      { id: yeniId, banka_hesap_id: '', banka_adi: '', para_birimi: 'TL', bakiye: '' }
    ]);
  };

  const handleSatirSil = (id) => {
    if (bakiyeSatirlari.length === 1) {
      alert('En az bir satır olmalı');
      return;
    }
    setBakiyeSatirlari(bakiyeSatirlari.filter(s => s.id !== id));
  };

  const handleSatirChange = (id, field, value) => {
    setBakiyeSatirlari(bakiyeSatirlari.map(s => {
      if (s.id === id) {
        if (field === 'banka_hesap_id') {
          const secilenHesap = hesaplar.find(h => h.id === value);
          return {
            ...s,
            banka_hesap_id: value,
            banka_adi: secilenHesap ? secilenHesap.kisa_ad : ''
          };
        }
        return { ...s, [field]: value };
      }
      return s;
    }));
  };

  const handleBakiyeKaydet = async () => {
    const doluSatirlar = bakiyeSatirlari.filter(s => 
      s.banka_adi && s.bakiye !== '' && s.bakiye !== null && parseFloat(s.bakiye) !== 0
    );
    
    if (doluSatirlar.length === 0) {
      alert('En az bir bakiye girmelisiniz');
      return;
    }

    const hasUSD = doluSatirlar.some(s => s.para_birimi === 'USD');
    const hasEUR = doluSatirlar.some(s => s.para_birimi === 'EUR');
    
    if (hasUSD && kurUsd <= 0) {
      alert('USD bakiyesi girdiniz, lütfen USD kurunu girin');
      return;
    }
    if (hasEUR && kurEur <= 0) {
      alert('EUR bakiyesi girdiniz, lütfen EUR kurunu girin');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        bakiyeler: doluSatirlar.map(s => ({
          banka_hesap_id: s.banka_hesap_id || '',
          banka_adi: s.banka_adi,
          para_birimi: s.para_birimi,
          bakiye: parseFloat(s.bakiye) || 0,
          tl_karsiligi: 0
        })),
        notlar: notlar,
        kur_usd: parseFloat(kurUsd) || 0,
        kur_eur: parseFloat(kurEur) || 0
      };

      const response = await axios.post(`${API}/gunluk-bakiye`, payload);
      
      if (response.data.success) {
        alert('✅ Bakiyeler kaydedildi ve rapor mail olarak gönderildi!');
        setBakiyeSatirlari([
          { id: 1, banka_hesap_id: '', banka_adi: '', para_birimi: 'TL', bakiye: '' }
        ]);
        setNotlar('');
        fetchBakiyeGecmisi();
      }
    } catch (error) {
      console.error('Bakiye kaydedilirken hata:', error);
      alert('Bakiye kaydedilemedi: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

// === BAKIYE GEÇMİŞİ DÜZENLEME/SİLME ===
  const handleBakiyeGecmisiEdit = (kayit) => {
    setEditingBakiye(kayit);
    setEditBakiyeSatirlari(kayit.bakiyeler.map((b, idx) => ({
      id: idx + 1,
      banka_hesap_id: b.banka_hesap_id || '',
      banka_adi: b.banka_adi,
      para_birimi: b.para_birimi,
      bakiye: b.bakiye
    })));
    setEditKurUsd(kayit.kur_usd || 0);
    setEditKurEur(kayit.kur_eur || 0);
    setEditNotlar(kayit.notlar || '');
    setShowEditBakiyeModal(true);
  };

  const handleBakiyeGecmisiDelete = async (kayit) => {
    const tarihStr = formatTarih(kayit.tarih);
    if (window.confirm(`${tarihStr} tarihli bakiye kaydını silmek istediğinize emin misiniz?\n\nBu işlem geri alınamaz ve mail ile bildirilecektir.`)) {
      try {
        await axios.delete(`${API}/gunluk-bakiye/${kayit.id}`);
        alert('✅ Bakiye kaydı silindi ve bildirim maili gönderildi!');
        fetchBakiyeGecmisi();
      } catch (error) {
        console.error('Bakiye silinirken hata:', error);
        alert('Bakiye kaydı silinemedi: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const handleEditSatirChange = (id, field, value) => {
    setEditBakiyeSatirlari(editBakiyeSatirlari.map(s => {
      if (s.id === id) {
        return { ...s, [field]: value };
      }
      return s;
    }));
  };

  const handleEditSatirEkle = () => {
    const yeniId = Math.max(...editBakiyeSatirlari.map(s => s.id), 0) + 1;
    setEditBakiyeSatirlari([...editBakiyeSatirlari, { id: yeniId, banka_hesap_id: '', banka_adi: '', para_birimi: 'TL', bakiye: '' }]);
  };

  const handleEditSatirSil = (id) => {
    if (editBakiyeSatirlari.length === 1) { alert('En az bir satır olmalı'); return; }
    setEditBakiyeSatirlari(editBakiyeSatirlari.filter(s => s.id !== id));
  };

  const handleBakiyeGuncelle = async () => {
    const doluSatirlar = editBakiyeSatirlari.filter(s => s.banka_adi && s.bakiye !== '' && s.bakiye !== null);
    if (doluSatirlar.length === 0) { alert('En az bir bakiye girmelisiniz'); return; }

    const hasUSD = doluSatirlar.some(s => s.para_birimi === 'USD');
    const hasEUR = doluSatirlar.some(s => s.para_birimi === 'EUR');
    if (hasUSD && editKurUsd <= 0) { alert('USD bakiyesi var, lütfen USD kurunu girin'); return; }
    if (hasEUR && editKurEur <= 0) { alert('EUR bakiyesi var, lütfen EUR kurunu girin'); return; }

    setSaving(true);
    try {
      const payload = {
        bakiyeler: doluSatirlar.map(s => ({
          banka_hesap_id: s.banka_hesap_id || '',
          banka_adi: s.banka_adi,
          para_birimi: s.para_birimi,
          bakiye: parseFloat(s.bakiye) || 0,
          tl_karsiligi: 0
        })),
        notlar: editNotlar,
        kur_usd: parseFloat(editKurUsd) || 0,
        kur_eur: parseFloat(editKurEur) || 0
      };
      const response = await axios.put(`${API}/gunluk-bakiye/${editingBakiye.id}`, payload);
      if (response.data.success) {
        alert('✅ Bakiye güncellendi ve bildirim maili gönderildi!');
        setShowEditBakiyeModal(false);
        setEditingBakiye(null);
        fetchBakiyeGecmisi();
      }
    } catch (error) {
      alert('Bakiye güncellenemedi: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  // Toplam hesaplama
  const hesaplaToplam = () => {
    let toplamTL = 0;
    let toplamUSD = 0;
    let toplamEUR = 0;

    bakiyeSatirlari.forEach(s => {
      const bakiye = parseFloat(s.bakiye) || 0;
      if (s.para_birimi === 'TL') toplamTL += bakiye;
      else if (s.para_birimi === 'USD') toplamUSD += bakiye;
      else if (s.para_birimi === 'EUR') toplamEUR += bakiye;
    });

    const usdTL = toplamUSD * (parseFloat(kurUsd) || 0);
    const eurTL = toplamEUR * (parseFloat(kurEur) || 0);
    const genelToplam = toplamTL + usdTL + eurTL;

    return { toplamTL, toplamUSD, toplamEUR, usdTL, eurTL, genelToplam };
  };

  // Grafik için veri hazırlama
  const prepareChartData = () => {
    if (!bakiyeGecmisi || bakiyeGecmisi.length === 0) return [];
    
    // Tarihe göre sırala (eskiden yeniye)
    const sortedData = [...bakiyeGecmisi].sort((a, b) => 
      new Date(a.tarih) - new Date(b.tarih)
    );

    return sortedData.map(kayit => ({
      tarih: new Date(kayit.tarih).toLocaleDateString('tr-TR', {
        day: '2-digit',
        month: '2-digit'
      }),
      fullTarih: new Date(kayit.tarih).toLocaleDateString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }),
      TL: kayit.toplam_tl || 0,
      USD: kayit.toplam_usd || 0,
      EUR: kayit.toplam_eur || 0,
      'Genel Toplam (TL)': kayit.genel_toplam_tl || 0
    }));
  };

  const formatTarih = (tarihStr) => {
    if (!tarihStr) return '-';
    const tarih = new Date(tarihStr);
    return tarih.toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPara = (miktar) => {
    return new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(miktar || 0);
  };

  // Grafik tooltip formatı
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          background: 'white',
          padding: '15px',
          border: '1px solid #ddd',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
        }}>
          <p style={{ fontWeight: 600, marginBottom: '10px', color: '#00296B' }}>
            {payload[0]?.payload?.fullTarih || label}
          </p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color, margin: '5px 0' }}>
              {entry.name}: {formatPara(entry.value)} {entry.name.includes('TL') ? '' : entry.name}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return <div className="loading">Yükleniyor...</div>;
  }

  const toplamlar = hesaplaToplam();
  const chartData = prepareChartData();

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">💰 Banka Hesapları</h1>
        <p className="page-subtitle">Banka hesaplarını yönetin ve günlük bakiye girişi yapın</p>
      </div>

      {/* Tab Menüsü */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <button
          className={`btn ${activeTab === 'bakiye' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveTab('bakiye')}
        >
          📝 Günlük Bakiye Girişi
        </button>
        <button
          className={`btn ${activeTab === 'hesaplar' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveTab('hesaplar')}
        >
          🏦 Banka Hesapları
        </button>
        <button
          className={`btn ${activeTab === 'gecmis' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveTab('gecmis')}
        >
          📊 Bakiye Geçmişi
        </button>
        <button
          className={`btn ${activeTab === 'grafik' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveTab('grafik')}
        >
          📈 Grafik
        </button>
      </div>

      {/* Günlük Bakiye Girişi Tab */}
      {activeTab === 'bakiye' && (
        <div>
          {/* Kur Bilgileri */}
          <div className="card" style={{ marginBottom: '24px' }}>
            <h3 style={{ marginTop: 0 }}>💱 Döviz Kurları</h3>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">USD Kuru (TL)</label>
                <input
                  type="number"
                  className="form-input"
                  value={kurUsd}
                  onChange={(e) => setKurUsd(e.target.value)}
                  step="0.0001"
                />
              </div>
              <div className="form-group">
                <label className="form-label">EUR Kuru (TL)</label>
                <input
                  type="number"
                  className="form-input"
                  value={kurEur}
                  onChange={(e) => setKurEur(e.target.value)}
                  step="0.0001"
                />
              </div>
            </div>
          </div>

          {/* Bakiye Giriş Tablosu */}
          <div className="card" style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0 }}>📝 Bakiye Girişi</h3>
              <button 
                className="btn btn-primary"
                onClick={handleSatirEkle}
                style={{ padding: '8px 16px' }}
              >
                ➕ Satır Ekle
              </button>
            </div>
            
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: '30%' }}>Banka</th>
                  <th style={{ width: '15%' }}>Para Birimi</th>
                  <th style={{ width: '20%' }}>Bakiye</th>
                  <th style={{ width: '20%' }}>TL Karşılığı</th>
                  <th style={{ width: '15%' }}>İşlem</th>
                </tr>
              </thead>
              <tbody>
                {bakiyeSatirlari.map((satir) => {
                  const bakiye = parseFloat(satir.bakiye) || 0;
                  let tlKarsiligi = bakiye;
                  if (satir.para_birimi === 'USD') tlKarsiligi = bakiye * (parseFloat(kurUsd) || 0);
                  else if (satir.para_birimi === 'EUR') tlKarsiligi = bakiye * (parseFloat(kurEur) || 0);

                  return (
                    <tr key={satir.id}>
                      <td>
                        {hesaplar.length > 0 ? (
                          <select
                            className="form-select"
                            value={satir.banka_hesap_id}
                            onChange={(e) => handleSatirChange(satir.id, 'banka_hesap_id', e.target.value)}
                            style={{ width: '100%' }}
                          >
                            <option value="">-- Banka Seçin --</option>
                            {hesaplar.map(h => (
                              <option key={h.id} value={h.id}>{h.kisa_ad}</option>
                            ))}
                            <option value="diger">📝 Elle Gir...</option>
                          </select>
                        ) : (
                          <input
                            type="text"
                            className="form-input"
                            value={satir.banka_adi}
                            onChange={(e) => handleSatirChange(satir.id, 'banka_adi', e.target.value)}
                            placeholder="Banka adı"
                            style={{ width: '100%' }}
                          />
                        )}
                        {satir.banka_hesap_id === 'diger' && (
                          <input
                            type="text"
                            className="form-input"
                            value={satir.banka_adi}
                            onChange={(e) => handleSatirChange(satir.id, 'banka_adi', e.target.value)}
                            placeholder="Banka adını yazın..."
                            style={{ width: '100%', marginTop: '8px' }}
                          />
                        )}
                      </td>
                      <td>
                        <select
                          className="form-select"
                          value={satir.para_birimi}
                          onChange={(e) => handleSatirChange(satir.id, 'para_birimi', e.target.value)}
                          style={{ width: '100%' }}
                        >
                          <option value="TL">TL</option>
                          <option value="USD">USD</option>
                          <option value="EUR">EUR</option>
                          <option value="GBP">GBP</option>
                        </select>
                      </td>
                      <td>
                        <input
                          type="number"
                          className="form-input"
                          value={satir.bakiye}
                          onChange={(e) => handleSatirChange(satir.id, 'bakiye', e.target.value)}
                          placeholder="0.00"
                          step="0.01"
                          style={{ width: '100%' }}
                        />
                      </td>
                      <td style={{ fontWeight: 500, color: '#00296B' }}>
                        {satir.bakiye ? `${formatPara(tlKarsiligi)} TL` : '-'}
                      </td>
                      <td>
                        <button
                          className="btn btn-danger"
                          onClick={() => handleSatirSil(satir.id)}
                          style={{ padding: '6px 12px', fontSize: '14px' }}
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Yeni Satır Ekleme Butonu */}
            <div style={{ marginTop: '10px' }}>
              <button 
                className="btn btn-secondary"
                onClick={handleSatirEkle}
                style={{ width: '100%', padding: '12px', border: '2px dashed #ccc' }}
              >
                ➕ Yeni Banka Ekle
              </button>
            </div>

            {/* Toplam Özeti */}
            <div style={{ 
              marginTop: '20px', 
              padding: '20px', 
              background: '#f8f9fa', 
              borderRadius: '8px',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '15px'
            }}>
              <div>
                <div style={{ fontSize: '12px', color: '#666' }}>Toplam TL</div>
                <div style={{ fontSize: '18px', fontWeight: 600 }}>{formatPara(toplamlar.toplamTL)} TL</div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#666' }}>Toplam USD</div>
                <div style={{ fontSize: '18px', fontWeight: 600 }}>{formatPara(toplamlar.toplamUSD)} USD</div>
                <div style={{ fontSize: '12px', color: '#888' }}>= {formatPara(toplamlar.usdTL)} TL</div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#666' }}>Toplam EUR</div>
                <div style={{ fontSize: '18px', fontWeight: 600 }}>{formatPara(toplamlar.toplamEUR)} EUR</div>
                <div style={{ fontSize: '12px', color: '#888' }}>= {formatPara(toplamlar.eurTL)} TL</div>
              </div>
              <div style={{ 
                background: '#FDC500', 
                padding: '15px', 
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '12px', color: '#00296B' }}>GENEL TOPLAM</div>
                <div style={{ fontSize: '24px', fontWeight: 700, color: '#00296B' }}>
                  {formatPara(toplamlar.genelToplam)} TL
                </div>
              </div>
            </div>

            {/* Notlar */}
            <div className="form-group" style={{ marginTop: '20px' }}>
              <label className="form-label">Notlar (Opsiyonel)</label>
              <textarea
                className="form-textarea"
                value={notlar}
                onChange={(e) => setNotlar(e.target.value)}
                rows={3}
                placeholder="Eklemek istediğiniz notlar..."
              />
            </div>

            {/* Kaydet Butonu */}
            <div style={{ marginTop: '20px' }}>
              <button 
                className="btn btn-primary" 
                onClick={handleBakiyeKaydet}
                disabled={saving}
                style={{ padding: '12px 30px', fontSize: '16px' }}
              >
                {saving ? '⏳ Kaydediliyor...' : '💾 Kaydet ve Mail Gönder'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Banka Hesapları Tab */}
      {activeTab === 'hesaplar' && (
        <div>
          <div style={{ marginBottom: '24px' }}>
            <button
              className="btn btn-primary"
              onClick={() => {
                if (showHesapForm) {
                  handleFormCancel();
                } else {
                  setShowHesapForm(true);
                }
              }}
            >
              {showHesapForm ? 'İptal' : '+ Yeni Banka Hesabı'}
            </button>
          </div>

          {showHesapForm && (
            <div className="card" style={{ marginBottom: '24px' }}>
              <h3 style={{ marginTop: 0 }}>{editingHesapId ? '✏️ Banka Hesabı Düzenle' : '➕ Yeni Banka Hesabı'}</h3>
              <form onSubmit={handleHesapSubmit}>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Kısa Ad *</label>
                    <input
                      type="text"
                      name="kisa_ad"
                      className="form-input"
                      value={hesapFormData.kisa_ad}
                      onChange={(e) => setHesapFormData({...hesapFormData, kisa_ad: e.target.value})}
                      required
                      placeholder="örn: Garanti TL, İş Bankası EUR"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Banka Adı *</label>
                    <input
                      type="text"
                      name="banka_adi"
                      className="form-input"
                      value={hesapFormData.banka_adi}
                      onChange={(e) => setHesapFormData({...hesapFormData, banka_adi: e.target.value})}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">IBAN *</label>
                  <input
                    type="text"
                    name="iban"
                    className="form-input"
                    value={hesapFormData.iban}
                    onChange={(e) => setHesapFormData({...hesapFormData, iban: e.target.value})}
                    required
                    placeholder="TR00 0000 0000 0000 0000 0000 00"
                  />
                </div>

                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">SWIFT</label>
                    <input
                      type="text"
                      name="swift"
                      className="form-input"
                      value={hesapFormData.swift}
                      onChange={(e) => setHesapFormData({...hesapFormData, swift: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Para Birimi</label>
                    <select
                      name="para_birimi"
                      className="form-select"
                      value={hesapFormData.para_birimi}
                      onChange={(e) => setHesapFormData({...hesapFormData, para_birimi: e.target.value})}
                    >
                      <option value="TL">TL</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                    </select>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '10px' }}>
                  <button type="submit" className="btn btn-primary">
                    {editingHesapId ? 'Güncelle' : 'Kaydet'}
                  </button>
                  {editingHesapId && (
                    <button type="button" className="btn btn-secondary" onClick={handleFormCancel}>
                      İptal
                    </button>
                  )}
                </div>
              </form>
            </div>
          )}

          {hesaplar.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
              <h3>Henüz banka hesabı kaydı yok</h3>
              <p>Yeni banka hesabı eklemek için yukarıdaki butona tıklayın</p>
              <p style={{ color: '#666', fontSize: '14px', marginTop: '10px' }}>
                💡 İpucu: Bakiye girişinde elle de banka adı yazabilirsiniz
              </p>
            </div>
          ) : (
            <div className="card">
              <table className="table">
                <thead>
                  <tr>
                    <th>Kısa Ad</th>
                    <th>Banka Adı</th>
                    <th>IBAN</th>
                    <th>SWIFT</th>
                    <th>Para Birimi</th>
                    <th>İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {hesaplar.map(hesap => (
                    <tr key={hesap.id}>
                      <td style={{ fontWeight: 600 }}>{hesap.kisa_ad}</td>
                      <td>{hesap.banka_adi}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{hesap.iban}</td>
                      <td>{hesap.swift || '-'}</td>
                      <td>{hesap.para_birimi}</td>
                      <td>
                        <button 
                          className="btn btn-secondary" 
                          onClick={() => handleHesapEdit(hesap)}
                          style={{ padding: '5px 10px', fontSize: '12px', marginRight: '5px' }}
                        >
                          ✏️ Düzenle
                        </button>
                        <button 
                          className="btn btn-danger" 
                          onClick={() => handleHesapDelete(hesap.id)}
                          style={{ padding: '5px 10px', fontSize: '12px' }}
                        >
                          🗑️ Sil
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Bakiye Geçmişi Tab */}
      {activeTab === 'gecmis' && (
        <div>
          {bakiyeGecmisi.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
              <h3>Henüz bakiye kaydı yok</h3>
              <p>Günlük bakiye girişi yaptığınızda burada görüntülenecek</p>
            </div>
          ) : (
            <div className="card">
              <table className="table">
                <thead>
                  <tr>
                    <th>Tarih</th>
                    <th>Oluşturan</th>
                    <th>TL</th>
                    <th>USD</th>
                    <th>EUR</th>
                    <th>Genel Toplam (TL)</th>
                    <th>Detay</th>
                  </tr>
                </thead>
                <tbody>
                  {bakiyeGecmisi.map(kayit => (
                    <tr key={kayit.id}>
                      <td style={{ fontWeight: 500 }}>{formatTarih(kayit.tarih)}</td>
                      <td>{kayit.olusturan}</td>
                      <td>{formatPara(kayit.toplam_tl)} TL</td>
                      <td>{formatPara(kayit.toplam_usd)} USD</td>
                      <td>{formatPara(kayit.toplam_eur)} EUR</td>
                      <td style={{ fontWeight: 700, color: '#00296B' }}>
                        {formatPara(kayit.genel_toplam_tl)} TL
                      </td>
<td>
                        <div style={{ display: 'flex', gap: '5px' }}>
                          <button 
                            className="btn btn-secondary"
                            onClick={() => {
                              const detay = kayit.bakiyeler.map(b => 
                                `${b.banka_adi}: ${formatPara(b.bakiye)} ${b.para_birimi}`
                              ).join('\n');
                              alert(`Bakiye Detayları (${formatTarih(kayit.tarih)})\n\n${detay}\n\nKurlar:\nUSD: ${kayit.kur_usd}\nEUR: ${kayit.kur_eur}`);
                            }}
                            style={{ padding: '5px 10px', fontSize: '12px' }}
                          >
                            👁️
                          </button>
                          <button 
                            className="btn btn-primary"
                            onClick={() => handleBakiyeGecmisiEdit(kayit)}
                            style={{ padding: '5px 10px', fontSize: '12px' }}
                          >
                            ✏️
                          </button>
                          <button 
                            className="btn btn-danger"
                            onClick={() => handleBakiyeGecmisiDelete(kayit)}
                            style={{ padding: '5px 10px', fontSize: '12px' }}
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Grafik Tab */}
      {activeTab === 'grafik' && (
        <div>
          {chartData.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
              <h3>📊 Grafik için veri yok</h3>
              <p>Günlük bakiye girişi yaptığınızda grafikler burada görüntülenecek</p>
            </div>
          ) : (
            <>
              {/* Genel Toplam Grafiği */}
              <div className="card" style={{ marginBottom: '24px' }}>
                <h3 style={{ marginTop: 0 }}>📈 Genel Toplam (TL) Trendi</h3>
                <div style={{ width: '100%', height: 350 }}>
                  <ResponsiveContainer>
                    <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 20, bottom: 5 }}>
                      <defs>
                        <linearGradient id="colorToplam" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#00296B" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#00296B" stopOpacity={0.1}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis dataKey="tarih" tick={{ fontSize: 12 }} />
                      <YAxis 
                        tick={{ fontSize: 12 }} 
                        tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Area 
                        type="monotone" 
                        dataKey="Genel Toplam (TL)" 
                        stroke="#00296B" 
                        fillOpacity={1} 
                        fill="url(#colorToplam)" 
                        strokeWidth={2}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Para Birimi Karşılaştırma Grafiği */}
              <div className="card" style={{ marginBottom: '24px' }}>
                <h3 style={{ marginTop: 0 }}>💱 Para Birimi Bazında Bakiyeler</h3>
                <div style={{ width: '100%', height: 350 }}>
                  <ResponsiveContainer>
                    <LineChart data={chartData} margin={{ top: 10, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis dataKey="tarih" tick={{ fontSize: 12 }} />
                      <YAxis 
                        tick={{ fontSize: 12 }} 
                        tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="TL" 
                        stroke="#00296B" 
                        strokeWidth={2}
                        dot={{ fill: '#00296B', strokeWidth: 2 }}
                        activeDot={{ r: 6 }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="USD" 
                        stroke="#2E7D32" 
                        strokeWidth={2}
                        dot={{ fill: '#2E7D32', strokeWidth: 2 }}
                        activeDot={{ r: 6 }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="EUR" 
                        stroke="#1565C0" 
                        strokeWidth={2}
                        dot={{ fill: '#1565C0', strokeWidth: 2 }}
                        activeDot={{ r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Özet Kartlar */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                gap: '20px' 
              }}>
                <div className="card" style={{ textAlign: 'center', padding: '20px' }}>
                  <div style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>📅 İlk Kayıt</div>
                  <div style={{ fontSize: '16px', fontWeight: 600 }}>{chartData[0]?.fullTarih}</div>
                  <div style={{ fontSize: '20px', fontWeight: 700, color: '#00296B', marginTop: '5px' }}>
                    {formatPara(chartData[0]?.['Genel Toplam (TL)'])} TL
                  </div>
                </div>
                <div className="card" style={{ textAlign: 'center', padding: '20px' }}>
                  <div style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>📅 Son Kayıt</div>
                  <div style={{ fontSize: '16px', fontWeight: 600 }}>{chartData[chartData.length - 1]?.fullTarih}</div>
                  <div style={{ fontSize: '20px', fontWeight: 700, color: '#00296B', marginTop: '5px' }}>
                    {formatPara(chartData[chartData.length - 1]?.['Genel Toplam (TL)'])} TL
                  </div>
                </div>
                <div className="card" style={{ 
                  textAlign: 'center', 
                  padding: '20px',
                  background: chartData.length > 1 && 
                    chartData[chartData.length - 1]?.['Genel Toplam (TL)'] > chartData[0]?.['Genel Toplam (TL)'] 
                    ? '#E8F5E9' : '#FFEBEE'
                }}>
                  <div style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>📊 Değişim</div>
                  {chartData.length > 1 ? (
                    <>
                      <div style={{ 
                        fontSize: '24px', 
                        fontWeight: 700, 
                        color: chartData[chartData.length - 1]?.['Genel Toplam (TL)'] > chartData[0]?.['Genel Toplam (TL)'] 
                          ? '#2E7D32' : '#C62828'
                      }}>
                        {chartData[chartData.length - 1]?.['Genel Toplam (TL)'] > chartData[0]?.['Genel Toplam (TL)'] ? '📈' : '📉'}
                        {' '}
                        {formatPara(
                          chartData[chartData.length - 1]?.['Genel Toplam (TL)'] - chartData[0]?.['Genel Toplam (TL)']
                        )} TL
                      </div>
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                        ({((chartData[chartData.length - 1]?.['Genel Toplam (TL)'] - chartData[0]?.['Genel Toplam (TL)']) / chartData[0]?.['Genel Toplam (TL)'] * 100).toFixed(1)}%)
                      </div>
                    </>
                  ) : (
                    <div style={{ color: '#666' }}>Karşılaştırma için en az 2 kayıt gerekli</div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Bakiye Düzenleme Modal */}
      {showEditBakiyeModal && editingBakiye && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '24px', width: '800px', maxWidth: '95%', maxHeight: '90vh', overflow: 'auto' }}>
            <h2 style={{ marginTop: 0, color: '#f59e0b' }}>✏️ Bakiye Kaydını Düzenle</h2>
            <p style={{ color: '#666', marginBottom: '20px' }}><strong>Kayıt Tarihi:</strong> {formatTarih(editingBakiye.tarih)}</p>

            <div className="form-grid" style={{ marginBottom: '20px' }}>
              <div className="form-group">
                <label className="form-label">USD Kuru (TL)</label>
                <input type="number" className="form-input" value={editKurUsd} onChange={(e) => setEditKurUsd(e.target.value)} step="0.0001" />
              </div>
              <div className="form-group">
                <label className="form-label">EUR Kuru (TL)</label>
                <input type="number" className="form-input" value={editKurEur} onChange={(e) => setEditKurEur(e.target.value)} step="0.0001" />
              </div>
            </div>

            <table className="table" style={{ marginBottom: '20px' }}>
              <thead><tr><th>Banka</th><th>Para Birimi</th><th>Bakiye</th><th>İşlem</th></tr></thead>
              <tbody>
                {editBakiyeSatirlari.map((satir) => (
                  <tr key={satir.id}>
                    <td><input type="text" className="form-input" value={satir.banka_adi} onChange={(e) => handleEditSatirChange(satir.id, 'banka_adi', e.target.value)} style={{ width: '100%' }} /></td>
                    <td>
                      <select className="form-select" value={satir.para_birimi} onChange={(e) => handleEditSatirChange(satir.id, 'para_birimi', e.target.value)} style={{ width: '100%' }}>
                        <option value="TL">TL</option><option value="USD">USD</option><option value="EUR">EUR</option><option value="GBP">GBP</option>
                      </select>
                    </td>
                    <td><input type="number" className="form-input" value={satir.bakiye} onChange={(e) => handleEditSatirChange(satir.id, 'bakiye', e.target.value)} step="0.01" style={{ width: '100%' }} /></td>
                    <td><button className="btn btn-danger" onClick={() => handleEditSatirSil(satir.id)} style={{ padding: '6px 12px' }}>🗑️</button></td>
                  </tr>
                ))}
              </tbody>
            </table>

            <button className="btn btn-secondary" onClick={handleEditSatirEkle} style={{ marginBottom: '20px' }}>➕ Satır Ekle</button>

            <div className="form-group" style={{ marginBottom: '20px' }}>
              <label className="form-label">Notlar</label>
              <textarea className="form-textarea" value={editNotlar} onChange={(e) => setEditNotlar(e.target.value)} rows={3} />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => { setShowEditBakiyeModal(false); setEditingBakiye(null); }}>İptal</button>
              <button className="btn btn-primary" onClick={handleBakiyeGuncelle} disabled={saving}>{saving ? '⏳ Kaydediliyor...' : '💾 Güncelle ve Mail Gönder'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BankaHesaplari;
